import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import { LookupItem, LookupResult, LookupValue } from '../../proxy/models/looksup';
import { TableName } from '../../proxy/models/table-name-enum';
import { EntityType } from '../../proxy/models/entity-type.enum';
import { CommonApiService } from '../../proxy/services/common-api.service';
import { IndexDbData, IndexDbKeys } from '../../proxy/models/index-db-keys';
import { IndexDbService } from './indexDb.service';


@Injectable({
  providedIn: 'root',
})
export class AllLookupsService {
  private apiService = inject(CommonApiService);
  private indexDbService = inject(IndexDbService);


  private dataSubject = new BehaviorSubject<LookupItem[]>([]);
  public data$ = this.dataSubject.asObservable();
  private isLookupsReadySubject$ = new ReplaySubject<boolean>(1);
  public isLookupsReady = this.isLookupsReadySubject$.asObservable();

  public getAllLookups(): Observable<LookupResult> {
    return this.apiService.executeQuery<any>(EntityType?.lookup, {area: 'Survey'});
  }

  public setLookups(data: { value: LookupItem[], checkSumValue?: null | string }): Observable<LookupResult> {
    const result = new ReplaySubject<any>(1);

    this.indexDbService.add({
      key: IndexDbKeys.LookupChecksum,
      value: data.value,
      checkSumValue: data?.checkSumValue
    })?.subscribe(({
      next: res => {
        this.dataSubject.next(data.value);
        this.setIsLookupReady();
        result.next(res);
      },
      error: err => result.error(err),
      complete: () => result.complete()
    }));

    return result.asObservable();
  }

  public setIsLookupReady(): void {
    this.isLookupsReadySubject$.next(true);
  }

  public removeIsLookupReady(): void {
    this.isLookupsReadySubject$.next(false);
  }

  public getLookups(): Observable<IndexDbData<LookupItem[]>> {
    return this.indexDbService.getByKey(IndexDbKeys.LookupChecksum);
  }

  public getLookupsSubjectValue(): LookupItem[] {
    return this.dataSubject.value;
  }

  public getLookupsByTableName(tableName: TableName): LookupValue[] {
    return this.getLookupsSubjectValue()?.find((el) => {
      return el.TableName === tableName;
    })?.LookupValue || [];
  }

  public getLookupByValue(table: TableName, value: string): LookupValue | undefined {
    return this.getLookupsSubjectValue()?.find((item) => {
      return item?.TableName == table;
    })?.LookupValue.find((el) => {
      return el.LookupName === value;
    });
  }

  public getLookupById(table: TableName, id: string): LookupValue | undefined {
    return this.getLookupsSubjectValue()?.find((item) => {
      return item?.TableName == table;
    })?.LookupValue.find((el) => {
      return el.LookupValueId === id;
    });
  }

  public removeLookups(): void {
    this.indexDbService.removeByKey(IndexDbKeys.LookupChecksum);
    this.removeIsLookupReady();
  }
}
